/**
 * Author: Codal
 * Updater: Ben Quackenbush
 * Updated Date: 12/29/2025
 * Description: Adds functionality to the Klevu powered search page.
 * Latest Update: Added a Shopify GraphQL call to fetch extra product information for use in tw-klevu-variant-picker.js
 *    Updated klevu-templates.liquid. Templates updated are,
 *    klevuLandingTemplateResults,
 *    klevuLandingTemplateProductBlock,
 *    landingPageProductColorSwatches,
 *    landingPageProductAddToCart
 */
import { productsInfoQuery, fetchGraphQL } from '@theme/tw-graphql';
function initCardListSwiperWrapper() {
  const containers = document.querySelectorAll(
    '.tw-carousel-search-products-swiper',
  );
  if (!containers || containers.length === 0) return;

  let swipers = [];

  const destroySwipers = () => {
    swipers.forEach((swiper) => swiper.destroy(true, true));
    swipers = [];
  };

  const initSwipers = (isXl, isMd) => {
    destroySwipers();

    containers.forEach((container) => {
      const parent = container.closest('.tw-carousel-search-products');
      if (!parent) return;

      const swiper = new window.Swiper(container, {
        slidesPerView: isXl ? 4 : isMd ? 2.5 : 1.125,
        spaceBetween: 24,
        navigation: {
          nextEl: parent.querySelector('.swiper-buttons .swiper-button-next'),
          prevEl: parent.querySelector('.swiper-buttons .swiper-button-prev'),
        },
        pagination: {
          el: container.querySelector('.swiper-pagination'),
          clickable: true,
          type: 'progressbar',
        },
      });

      swipers.push(swiper);
    });
  };

  // Set up media query
  const desktopMediaQuery = window.matchMedia('(min-width: 1280px)');

  const handleMediaChange = (e) => {
    initSwipers(e.matches);
  };

  if (desktopMediaQuery.addEventListener) {
    desktopMediaQuery.addEventListener('change', handleMediaChange);
  } else if (desktopMediaQuery.addListener) {
    // For older browsers
    desktopMediaQuery.addListener(handleMediaChange);
  }

  initSwipers(desktopMediaQuery.matches);
}
function waitForAfterTemplateRender() {
  return new Promise((resolve) => {
    let afterTemplateRenderCalled = false;
    window?.klevu?.afterTemplateRender('landing', function (data, scope) {
      afterTemplateRenderCalled = true;
      resolve({ data, scope });
    });

    setTimeout(() => {
      if (!afterTemplateRenderCalled) {
        resolve(null);
      }
    }, 300);
  });
}
function bindVariantSelection() {
  const variantButtons = document.querySelectorAll('[id^="kuSwatchLink"]');
  if (variantButtons) {
    [...variantButtons].forEach((currentButton) => {
      currentButton.addEventListener('click', () => {
        if (!currentButton.classList.contains('active')) {
          currentButton.classList.add('active');
        }
        const parentCard = currentButton.closest('.kuProdWrap');

        if (parentCard) {
          const buttonsInCard = parentCard.querySelectorAll(
            '[id^="kuSwatchLink"]',
          );
          if (buttonsInCard) {
            [...buttonsInCard].forEach((button) => {
              if (button !== currentButton) {
                button.classList.remove('active');
              }
            });
          }
          const links = parentCard.querySelectorAll('a');
          if (links) {
            [...links].forEach((link) => {
              const href = new URL(link.href);
              href.searchParams.set('variant', currentButton.dataset.variant);
              link.href = href.toString();
            });
          }
          const input = parentCard.querySelector(
            'product-form-component input[ref="variantId"]',
          );

          if (input) {
            input.value = currentButton.dataset.variant;
          }
        }
      });
    });
  }
}

function initOptionSwipers() {
  // Destroy any previous instances - as variant selection triggers a DOM morph so we want to reinitialise all our option swipers again
  if (window.optionSwipers) {
    window.optionSwipers.forEach((swiper) => swiper.destroy(true, true));
  }

  window.optionSwipers = [];

  // Initialize Swiper for each container
  document.querySelectorAll('.optionSwiper').forEach((container) => {
    const space = container.id.includes('size') ? 8 : 4;

    const swiper = new Swiper(container, {
      slidesPerView: 'auto',
      spaceBetween: space,
      watchOverflow: true,
      loop: false,
      freeMode: true,
      navigation: {
        prevEl: container
          .closest('.optionSwiper-container')
          .querySelector('.optionSwiper-button-prev'),
        nextEl: container
          .closest('.optionSwiper-container')
          .querySelector('.optionSwiper-button-next'),
      },
    });

    window.optionSwipers.push(swiper);
  });
}
function getCurrentTabbedPage() {
  const wrapper = document.querySelector('.klevuWrap');
  let type = undefined;
  if (wrapper) {
    if (wrapper.classList.contains('productListActive')) {
      type = 'productList';
    } else if (wrapper.classList.contains('contentListActive')) {
      type = 'contentList';
    }
  }
  return type;
}
const updateSortLabel = (currentPageResults) => {
  const pageType = getCurrentTabbedPage();

  if (!pageType || !currentPageResults) return;

  if (pageType == 'productList') {
    const term = document.querySelector(
      '.klevuWrap.productListActive .productList .paginated_term',
    );
    const count = document.querySelector(
      '.klevuWrap.productListActive .productList .paginated_count',
    );
    if (!term || !count) return;
    term.textContent = 'Products';
    count.textContent = currentPageResults;
  } else if (pageType === 'contentList') {
    const term = document.querySelector(
      '.klevuWrap.contentListActive .contentList .paginated_term',
    );
    const count = document.querySelector(
      '.klevuWrap.contentListActive .contentList .paginated_count',
    );
    if (!term || !count) return;
    term.textContent = 'Articles';
    count.textContent = currentPageResults;
  }
};

function handleTabChange(data) {
  const tabButtons = document.querySelectorAll('.kuTabs .kuTab');
  [...tabButtons].forEach((tab) => {
    tab.addEventListener('click', () => {
      const currentPageResults = data?.response?.data?.queryResults?.find(
        (result) => result.id === data.context.activeQueryId,
      )?.meta?.totalResultsFound;

      updateSortLabel(currentPageResults);

      window.matchHeights();
    });
  });
}

function handleKlevuRender(data) {
  initCardListSwiperWrapper();
  bindVariantSelection();
  const currentPageResults = data?.response?.data?.queryResults?.find(
    (result) => result.id === data.context.activeQueryId,
  )?.meta?.totalResultsFound;
  handleTabChange(data);

  initOptionSwipers();

  updateSortLabel(currentPageResults);
  setupMatchHeights({
    dataAttr: 'data-equalheights',
    gridItemClass: 'tw-equal-height-container',
  });
  const desktopMediaQuery = window.matchMedia('(min-width: 1280px)');

  const entryPointProductDesktop = document.getElementById(
    'desktop-filters-product',
  );
  const entryPointContentDesktop = document.getElementById(
    'desktop-filters-content',
  );
  const entryPointProductMobile = document.getElementById(
    'mobile-filters-product',
  );
  const entryPointContentMobile = document.getElementById(
    'mobile-filters-content',
  );

  if (!desktopMediaQuery.matches) {
    if (entryPointProductMobile && !entryPointProductMobile.firstElementChild) {
      entryPointProductMobile.appendChild(
        entryPointProductDesktop.firstElementChild,
      );
    }
    if (entryPointContentMobile && !entryPointContentMobile.firstElementChild) {
      entryPointContentMobile.appendChild(
        entryPointContentDesktop.firstElementChild,
      );
    }
  }

  desktopMediaQuery.addEventListener('change', (matchMedia) => {
    if (matchMedia.matches) {
      if (entryPointProductDesktop && entryPointProductMobile) {
        if (entryPointProductDesktop?.firstElementChild !== null) {
          entryPointProductDesktop.replaceChild(
            entryPointProductMobile?.firstElementChild,
            entryPointProductDesktop?.firstElementChild,
          );
        } else {
          entryPointProductDesktop.appendChild(
            entryPointProductMobile?.firstElementChild,
          );
        }
        entryPointProductMobile.innerHTML = '';
      }
      if (entryPointContentDesktop && entryPointContentMobile) {
        if (entryPointContentDesktop?.firstElementChild !== null) {
          entryPointContentDesktop.replaceChild(
            entryPointContentMobile?.firstElementChild,
            entryPointContentDesktop?.firstElementChild,
          );
        } else {
          entryPointContentDesktop.appendChild(
            entryPointContentMobile?.firstElementChild,
          );
        }
        entryPointContentMobile.innerHTML = '';
      }
    } else {
      if (entryPointProductDesktop && entryPointProductMobile) {
        if (entryPointProductMobile?.firstElementChild !== null) {
          entryPointProductMobile.replaceChild(
            entryPointProductDesktop?.firstElementChild,
            entryPointProductMobile?.firstElementChild,
          );
        } else {
          entryPointProductMobile?.appendChild(
            entryPointProductDesktop?.firstElementChild,
          );
        }
        entryPointProductDesktop.innerHTML = '';
      }
      if (entryPointContentDesktop && entryPointContentMobile) {
        if (entryPointContentMobile?.firstElementChild !== null) {
          entryPointContentMobile?.replaceChild(
            entryPointContentDesktop?.firstElementChild,
            entryPointContentMobile?.firstElementChild,
          );
        } else {
          entryPointContentMobile?.appendChild(
            entryPointContentDesktop?.firstElementChild,
          );
        }
        entryPointContentDesktop.innerHTML = '';
      }
    }
  });
}
klevu.afterTemplateRender('landing', async (data) => {
  window.productsInfo = window.productsInfo || {};
  const productsInfo = await fetchProductsInfo(data);
  // Products event allows the tw-klevu-variant-picker.js to render.
  dispatchProductsInfoEvent(productsInfo);
  handleKlevuRender(data);

  // Ensure Yotpo widgets are initialized after Klevu content is rendered
  if (
    window?.yotpoWidgetsContainer &&
    window?.yotpoWidgetsContainer?.initWidgets
  ) {
    window.yotpoWidgetsContainer.initWidgets();
  }
});

const fetchProductsInfo = async (data) => {
  const productListRecs = data?.response?.data?.queryResults?.find(
    (result) => result.id === 'productList',
  )?.records;
  if (!productListRecs || !Array.isArray(productListRecs)) return {};
  const filteredList = productListRecs.filter(({ itemGroupId }) => {
    return !window.productsInfo[itemGroupId];
  });
  if (!filteredList.length) return {};
  const productGids = filteredList.map(({ itemGroupId }) => {
    return `gid://shopify/Product/${itemGroupId}`;
  });
  const variables = {
    productIds: productGids,
  };
  const results = await fetchGraphQL(productsInfoQuery, variables);
  const productsInfo = {};
  const products = results?.data?.nodes;
  if (!products) return {};
  for (const product of products) {
    if (!product) continue;
    const { id } = product;
    const itemGroupId = id.split('/').pop();
    productsInfo[itemGroupId] = product;
  }

  return productsInfo;
};
// Used to update tw-klevu-variant-picker web components.
const dispatchProductsInfoEvent = (productsInfo) => {
  window.productsInfo = { ...window.productsInfo, ...productsInfo };
  const customEvent = new CustomEvent('productsInfoLoaded', {
    detail: productsInfo,
  });
  document.dispatchEvent(customEvent);
};
